import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  McSelect,
  McOption,
  McInput,
  McIcon,
} from "@maersk-global/mds-react-wrapper/components-core";
import FilterSelector from "../Home/FilterSelector";
import { getFilterOptions, getFilterValues } from "../../services/metricsApi";
import "./Analytics.css";

export const TREND_OPTIONS = [
  { value: "WEEKLY", label: "Last 7 Days" },
  { value: "MTD", label: "Month to Date" },
  { value: "QUARTERLY", label: "Quarter to Date" },
  { value: "YTD", label: "Year to Date" },
  { value: "YEARLY", label: "Year to Date (Yearly)" },
  { value: "ALL", label: "All Time" },
];

const DATE_RANGE_OPTIONS = [
  ...TREND_OPTIONS,
  { value: "CUSTOM_RANGE", label: "Custom Date Range" },
  { value: "SPECIFIC_YEAR", label: "Specific Year" },
  { value: "SPECIFIC_QUARTER", label: "Specific Quarter" },
  { value: "SPECIFIC_MONTH", label: "Specific Month" },
  { value: "SPECIFIC_DAY", label: "Specific Day" },
];

const CALENDAR_RANGE_FIELDS = {
  SPECIFIC_YEAR: ["year"],
  SPECIFIC_QUARTER: ["year", "quarter"],
  SPECIFIC_MONTH: ["year", "month"],
  SPECIFIC_DAY: ["year", "month", "day"],
};

const CUSTOM_DATE_FIELDS = ["created_date_from", "created_date_to"];

const FIELD_LABELS = {
  approver: "Approver",
  plant: "Plant",
  po_creator: "PO Creator",
  owner: "Owner",
  fo_number: "FO Number",
  segment: "Segment",
  mrs_non_mrs: "MRS/Non-MRS",
  material: "Material",
  region: "Region",
  year: "Year",
  quarter: "Quarter",
  month: "Month",
  day: "Day",
  created_date_from: "From Date",
  created_date_to: "To Date",
};

const NUMBER_FIELD_LIMITS = {
  year: { min: 1, max: 9999, step: 1 },
  quarter: { min: 1, max: 4, step: 1 },
  month: { min: 1, max: 12, step: 1 },
  day: { min: 1, max: 31, step: 1 },
};

// Fields rendered as a dropdown; options are the distinct values returned by
// /filter-values under the same key.
const SELECT_FIELDS = ["plant", "material", "approver", "po_creator", "segment", "mrs_non_mrs", "region"];

// These four come from /metrics/filter-options; the rest from /filter-values.
const FILTER_OPTIONS_FIELDS = ["plant", "material", "approver", "po_creator"];

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

// Tolerates key-casing/separator differences (e.g. "MRS_Non_MRS" vs "mrs_non_mrs").
const optionsFor = (json, field) => {
  const key = Object.keys(json || {}).find(
    (k) => normalizeKey(k) === normalizeKey(field) && Array.isArray(json[k])
  );
  return key ? json[key].filter((v) => v !== null && v !== undefined && v !== "").map(String) : [];
};

// mrs_non_mrs is exact-match against a raw Salesforce field on most
// dashboards (e.g. "MRS-AIR"), but on Material Spend & Cost Spike it's a
// case/space/hyphen-insensitive bucket match — only "MRS" / "Non-MRS" return
// non-empty results there (see docs/dashboard.md). Since the filter row is
// shared across all dashboards, the dropdown lists the raw values and this
// hint explains the exception instead of forcing one behavior.
const FIELD_HINTS = {
  mrs_non_mrs: "Material Spend & Cost Spike only matches the MRS / Non-MRS buckets; other values (e.g. MRS-AIR) return empty there.",
};

const emptyValues = (fields) => fields.reduce((acc, key) => ({ ...acc, [key]: "" }), {});
const emptyCalendarValues = () => ({ year: "", quarter: "", month: "", day: "" });
const emptyCustomDateValues = () => ({ created_date_from: "", created_date_to: "" });

// Shared filter row for the Analytics tabs: a trend preset + whichever common
// filters the current dashboard's endpoint supports (passed via `fields`),
// chosen via the same FilterSelector chooser used on Home.js.
// Trend changes apply immediately; text filters apply on "Apply" / Enter,
// matching the interaction the rest of the app already uses (Home.js).
const FilterBar = ({ fields, onApply, onClearAll }) => {
  const [trend, setTrend] = useState("ALL");
  const [dateRange, setDateRange] = useState("ALL");
  const [visibleFilters, setVisibleFilters] = useState([]);
  const [draft, setDraft] = useState(() => emptyValues(fields));
  const [applied, setApplied] = useState(() => emptyValues(fields));
  const [calendarDraft, setCalendarDraft] = useState(emptyCalendarValues);
  const [calendarApplied, setCalendarApplied] = useState({});
  const [customDateDraft, setCustomDateDraft] = useState(emptyCustomDateValues);
  const [customDateApplied, setCustomDateApplied] = useState({});
  const draftRef = useRef(draft);
  const calendarDraftRef = useRef(calendarDraft);
  const customDateDraftRef = useRef(customDateDraft);
  const [selectOptions, setSelectOptions] = useState({});

  useEffect(() => {
    const selectFields = SELECT_FIELDS.filter((f) => fields.includes(f));
    if (selectFields.length === 0) return;
    let cancelled = false;
    const needsOptions = selectFields.some((f) => FILTER_OPTIONS_FIELDS.includes(f));
    const needsValues = selectFields.some((f) => !FILTER_OPTIONS_FIELDS.includes(f));
    Promise.all([
      needsOptions ? getFilterOptions().catch((err) => { console.error("Error fetching /metrics/filter-options:", err); return {}; }) : {},
      needsValues ? getFilterValues().catch((err) => { console.error("Error fetching /filter-values:", err); return {}; }) : {},
    ]).then(([optionsJson, valuesJson]) => {
      if (cancelled) return;
      const next = {};
      selectFields.forEach((field) => {
        const source = FILTER_OPTIONS_FIELDS.includes(field) ? optionsJson : valuesJson;
        next[field] = [...new Set(optionsFor(source, field))];
      });
      setSelectOptions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fields]);

  const filterConfig = useMemo(
    () => fields.map((key) => ({ key, label: FIELD_LABELS[key] || key })),
    [fields]
  );

  const apply = (nextTrend, nextValues, nextDateRange = dateRange, nextCalendar = calendarDraftRef.current) => {
    const requiredCalendarFields = CALENDAR_RANGE_FIELDS[nextDateRange] || [];
    if (requiredCalendarFields.some((key) => !nextCalendar[key])) return;
    const calendarFilters = Object.fromEntries(requiredCalendarFields.map((key) => [key, nextCalendar[key]]));
    const customDateFilters = nextDateRange === "CUSTOM_RANGE"
      ? Object.fromEntries(CUSTOM_DATE_FIELDS.filter((key) => customDateDraftRef.current[key]).map((key) => [key, customDateDraftRef.current[key]]))
      : {};
    if (nextDateRange === "CUSTOM_RANGE" && Object.keys(customDateFilters).length === 0) return;
    setApplied(nextValues);
    setCalendarApplied(calendarFilters);
    setCustomDateApplied(customDateFilters);
    onApply({ trend: nextTrend, ...nextValues, ...calendarFilters, ...customDateFilters });
  };

  useEffect(() => {
    apply(trend, draftRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrendChange = (e) => {
    const value = e.detail?.value || "ALL";
    setDateRange(value);
    if (CALENDAR_RANGE_FIELDS[value] || value === "CUSTOM_RANGE") {
      setTrend("ALL");
      if (value === "CUSTOM_RANGE") return;
      apply("ALL", draftRef.current, value);
      return;
    }

    const clearedCalendar = emptyCalendarValues();
    calendarDraftRef.current = clearedCalendar;
    setCalendarDraft(clearedCalendar);
    setTrend(value);
    apply(value, draftRef.current, value, clearedCalendar);
  };

  const handleCalendarFieldChange = (key) => (e) => {
    calendarDraftRef.current = { ...calendarDraftRef.current, [key]: e.target?.value ?? "" };
    setCalendarDraft(calendarDraftRef.current);
  };

  const handleCustomDateChange = (key) => (e) => {
    customDateDraftRef.current = { ...customDateDraftRef.current, [key]: e.target?.value ?? "" };
    setCustomDateDraft(customDateDraftRef.current);
  };

  const handleFieldChange = (key) => (e) => {
    const value = e.target?.value ?? e.detail?.value ?? "";
    draftRef.current = { ...draftRef.current, [key]: value };
    setDraft(draftRef.current);
  };

  const handleSelectFieldChange = (key) => (e) => {
    const value = e.detail?.value ?? e.target?.value ?? "";
    draftRef.current = { ...draftRef.current, [key]: value };
    setDraft(draftRef.current);
    apply(trend, draftRef.current);
  };

  const handleApplyClick = () => apply(trend, draftRef.current);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleApplyClick();
  };

  const addFilter = (key) => {
    if (!visibleFilters.includes(key)) setVisibleFilters([...visibleFilters, key]);
  };

  const removeFilter = (key) => {
    setVisibleFilters(visibleFilters.filter((k) => k !== key));
    draftRef.current = { ...draftRef.current, [key]: "" };
    setDraft(draftRef.current);
    apply(trend, draftRef.current);
  };

  const handleClearAllFilters = () => {
    const cleared = emptyValues(fields);
    const clearedCalendar = emptyCalendarValues();
    const clearedCustomDates = emptyCustomDateValues();
    draftRef.current = cleared;
    calendarDraftRef.current = clearedCalendar;
    customDateDraftRef.current = clearedCustomDates;
    setVisibleFilters([]);
    setDraft(cleared);
    setCalendarDraft(clearedCalendar);
    setCustomDateDraft(clearedCustomDates);
    setDateRange("ALL");
    setTrend("ALL");
    apply("ALL", cleared, "ALL", clearedCalendar);
    if (onClearAll) onClearAll();
  };

  const calendarFields = CALENDAR_RANGE_FIELDS[dateRange] || [];
  const calendarRangeComplete = calendarFields.every((key) => calendarDraft[key]);
  const customDateRangeComplete = CUSTOM_DATE_FIELDS.some((key) => customDateDraft[key]);
  const activeChips = [...Object.entries(applied), ...Object.entries(calendarApplied), ...Object.entries(customDateApplied)].filter(([, v]) => v);

  return (
    <div className="analytics-filter-bar">
      <div className="analytics-filter-top-row" onKeyDown={handleKeyDown}>
        <McSelect
          label="Date Range"
          value={dateRange}
          optionselected={handleTrendChange}
          className="analytics-trend-select"
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <McOption key={opt.value} value={opt.value}>{opt.label}</McOption>
          ))}
        </McSelect>

        <FilterSelector
          filterConfig={filterConfig}
          visibleKeys={visibleFilters}
          onAdd={addFilter}
          onRemove={removeFilter}
          onClearAll={handleClearAllFilters}
          emptyStateText="Select filters to narrow the data."
        />
      </div>

      {calendarFields.length > 0 && (
        <div className="analytics-filter-row analytics-calendar-range-row" onKeyDown={handleKeyDown}>
          {calendarFields.map((key) => (
            <div key={key} className="analytics-filter-field">
              <McInput
                label={FIELD_LABELS[key]}
                placeholder={`Enter ${FIELD_LABELS[key].toLowerCase()}`}
                type="number"
                {...NUMBER_FIELD_LIMITS[key]}
                clearbutton
                value={calendarDraft[key]}
                input={handleCalendarFieldChange(key)}
                clearbuttonclick={() => handleCalendarFieldChange(key)({ target: { value: "" } })}
              />
            </div>
          ))}
        </div>
      )}

      {dateRange === "CUSTOM_RANGE" && (
        <div className="analytics-filter-row analytics-calendar-range-row" onKeyDown={handleKeyDown}>
          {CUSTOM_DATE_FIELDS.map((key) => (
            <div key={key} className="analytics-filter-field">
              <McInput
                label={FIELD_LABELS[key]}
                type="date"
                value={customDateDraft[key]}
                input={handleCustomDateChange(key)}
                clearbutton
                clearbuttonclick={() => handleCustomDateChange(key)({ target: { value: "" } })}
              />
            </div>
          ))}
        </div>
      )}

      {visibleFilters.length > 0 && (
        <div className="analytics-filter-row" onKeyDown={handleKeyDown}>
          {visibleFilters.map((key) => SELECT_FIELDS.includes(key) ? (
            <div key={key} className="analytics-filter-field">
              <McSelect
                label={FIELD_LABELS[key] || key}
                placeholder={`Select ${FIELD_LABELS[key] || key}`}
                value={draft[key]}
                optionselected={handleSelectFieldChange(key)}
              >
                {(selectOptions[key] || []).map((opt) => (
                  <McOption key={opt} value={opt}>{opt}</McOption>
                ))}
              </McSelect>
              
            </div>
          ) : (
            <div key={key} className="analytics-filter-field">
              <McInput
                label={FIELD_LABELS[key] || key}
                placeholder={`Enter ${FIELD_LABELS[key] || key}`}
                type={NUMBER_FIELD_LIMITS[key] ? "number" : "text"}
                {...NUMBER_FIELD_LIMITS[key]}
                clearbutton
                value={draft[key]}
                input={handleFieldChange(key)}
                clearbuttonclick={() => {
                  draftRef.current = { ...draftRef.current, [key]: "" };
                  setDraft(draftRef.current);
                }}
              />
              {FIELD_HINTS[key] && <span className="analytics-filter-field-hint">{FIELD_HINTS[key]}</span>}
            </div>
          ))}

        </div>
      )}

      {(visibleFilters.length > 0 || calendarFields.length > 0 || dateRange === "CUSTOM_RANGE") && (
        <div className="analytics-filter-row analytics-filter-actions-row">
          <button
            type="button"
            className="analytics-apply-btn"
            onClick={handleApplyClick}
            disabled={
              (calendarFields.length > 0 && !calendarRangeComplete) ||
              (dateRange === "CUSTOM_RANGE" && !customDateRangeComplete)
            }
          >
            Apply Filters
          </button>
        </div>
      )}

      {activeChips.length > 0 && (
        <div className="analytics-filter-chips">
          <span className="analytics-filter-chips-label">Filters applied:</span>
          {activeChips.map(([key, value]) => (
            <span key={key} className="analytics-filter-chip">
              <span className="analytics-filter-chip-text" title={String(value)}>{FIELD_LABELS[key] || key}: {value}</span>
              <McIcon
                icon="times"
                size="14"
                className="analytics-filter-chip-remove"
                onClick={() => {
                  if (Object.hasOwn(calendarApplied, key)) {
                    calendarDraftRef.current = { ...calendarDraftRef.current, [key]: "" };
                    setCalendarDraft(calendarDraftRef.current);
                    apply(trend, draftRef.current, dateRange, calendarDraftRef.current);
                  } else {
                    draftRef.current = { ...draftRef.current, [key]: "" };
                    setDraft(draftRef.current);
                    apply(trend, draftRef.current);
                  }
                }}
              />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
